import { PlusCircleIcon } from '@mm/icons';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectChatStarterRecordsByUiFilterKind } from '@mm/state/selectors/chatStarters';
import { StoreState } from '@mm/state/store-interfaces';
import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import { isFacebookConnectedSelector } from '@mm/state/selectors/userSelector';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useCookies } from 'react-cookie';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../common/components/GreenMarker/GreenMarker';
import { trackWebAppGAEvent } from '../../../../../../common/helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import { facebookRelatedStarterKinds, starterTypeByKind } from '../types';
import { useCreateChatStarter } from '../use-create-chat-starter';
import './CreateChatStarterButton.pcss';

type Props = {
  className?: string;
  starterKind: UIFilterStarterKindType;
  disabled?: boolean;
  withPlusIcon?: boolean;
  prepend?: string;
  buttonText?: string;
  showGreenMarker?: boolean;
  color?: ButtonColor;
};

export const DONT_SHOW_STORY_MENTION_POPUP = 'dont_show_story_mention_popup';

const CreateChatStarterButtonView = ({
  className,
  starterKind,
  withPlusIcon,
  prepend,
  disabled = false,
  buttonText,
  showGreenMarker,
  color,
}: Props) => {
  const { createChatStarter } = useCreateChatStarter();

  const isFacebookUser = useSelector(isFacebookConnectedSelector);
  const starters = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(state, starterKind),
  );
  const isDisabled =
    disabled || (starterKind === 'comment_guard' && !isFacebookUser);
  const [cookies, setCookie] = useCookies([DONT_SHOW_STORY_MENTION_POPUP]);
  const { openConfirmStoryMentionModal } = useModalActions();

  const createStarter = () => {
    trackWebAppGAEvent('click', {
      category: 'create_starter',
      label: `create_button_${starterKind}`,
    });
    createChatStarter(starterKind);
  };

  const onClick = () => {
    if (
      starterKind === 'instagram_story_mention' &&
      !!starters?.chatStarters.length &&
      !cookies[DONT_SHOW_STORY_MENTION_POPUP]
    ) {
      openConfirmStoryMentionModal(createStarter);
    } else {
      createStarter();
    }
  };

  const starter = starterTypeByKind[starterKind];
  let { name } = starter;

  if (starterKind === 'all') {
    name = 'Chat Starter';
  }

  const formattedName = pluralize.singular(name);
  return (
    <div className="create-chat-starter-button__container">
      {showGreenMarker && (
        <GreenMarker className="create-chat-starter-button__container--green-marker" />
      )}
      <PrimaryButton
        disabled={isDisabled}
        color={color}
        className={classNames('create-chat-starter-button', className)}
        leftIcon={withPlusIcon && <PlusCircleIcon />}
        onClick={onClick}
      >
        {buttonText ? (
          buttonText
        ) : (
          <>
            {prepend && `${prepend} `}
            {formattedName}
          </>
        )}
      </PrimaryButton>
    </div>
  );
};

export const CreateChatStarterButton = (props: Props) => {
  const { starterKind, disabled } = props;
  const starters = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(state, starterKind),
  );

  const isDisabled =
    disabled ||
    // when create flow is implemented for "all" starter, update its name in a map to 'Chat Starter', and use name condition in filter component only
    starterKind === 'all' ||
    (starterKind === 'facebook_page_welcomer' &&
      !!starters?.chatStarters.length) ||
    (starterKind === 'instagram_welcomer' && !!starters?.chatStarters.length);

  const shouldGuard = facebookRelatedStarterKinds.includes(starterKind);

  const { isWebChatActivated, isCommentGuardActivated } = useGreenCueMarkers();

  const showCustomerChatGreenMarker =
    starterKind === 'customer_chat_widget' && !isWebChatActivated;
  const showCommentGuardGreenMarker =
    starterKind === 'comment_guard' && !isCommentGuardActivated;
  const showGreenMarker =
    showCustomerChatGreenMarker || showCommentGuardGreenMarker;

  if (shouldGuard) {
    return (
      <TooltipGuard>
        {isAllowed => (
          <CreateChatStarterButtonView
            {...props}
            disabled={isDisabled || !isAllowed}
            showGreenMarker={showGreenMarker}
          />
        )}
      </TooltipGuard>
    );
  }

  return (
    <CreateChatStarterButtonView
      {...props}
      disabled={isDisabled}
      showGreenMarker={showGreenMarker}
    />
  );
};
